import React, { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import TextField from 'components/crm/ui/TextField';
import Spinner from 'components/crm/ui/Spinner';
import useText from 'hooks/useText';
import { formatSiret, searchCompanyDirectory } from 'helper/companyDirectory';
import { checkSirets } from 'server/crm/prospectionApi';

const MIN_CHARS = 3;
const DEBOUNCE_MS = 300;
const MAX_RESULTS = 6;

const CompanyDirectorySearch = ({ onPick, autoFocus }) => {
  const { text } = useText('prospection.addProspect.search');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [errorCode, setErrorCode] = useState(null);
  const [results, setResults] = useState([]);
  const [followedBySiret, setFollowedBySiret] = useState({});

  const trimmed = query.trim();

  useEffect(() => {
    if (trimmed.length < MIN_CHARS) {
      setStatus('idle');
      setResults([]);
      return undefined;
    }
    setStatus('loading');
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const found = (
          await searchCompanyDirectory(trimmed, { signal: controller.signal })
        ).slice(0, MAX_RESULTS);
        let followed = [];
        try {
          followed = await checkSirets(
            found.map((company) => company.siret).filter(Boolean),
          );
        } catch (error) {
          // Results stay usable without the mark: the api rejects duplicates anyway.
          if (error?.name === 'AbortError') return;
        }
        if (controller.signal.aborted) return;
        setFollowedBySiret(
          Object.fromEntries((followed ?? []).map((f) => [f.siret, f])),
        );
        setResults(found);
        setStatus('done');
      } catch (error) {
        if (error?.name === 'AbortError' || controller.signal.aborted) return;
        setErrorCode(
          error?.code === 'rateLimited' ? 'rateLimited' : 'unavailable',
        );
        setResults([]);
        setStatus('error');
      }
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [trimmed]);

  const open = trimmed.length >= MIN_CHARS;

  return (
    <div className="relative">
      <TextField
        id="company-directory-search"
        label={text('label')}
        icon={Search}
        placeholder={text('placeholder')}
        helper={text('hint')}
        value={query}
        autoFocus={autoFocus}
        autoComplete="off"
        onChange={(event) => setQuery(event.target.value)}
      />
      {open && (
        <div className="absolute inset-x-0 z-10 mt-1 overflow-hidden rounded-lg border border-line bg-white shadow-lg">
          {status === 'loading' && (
            <div
              role="status"
              aria-label={text('loading')}
              className="flex justify-center p-4"
            >
              <Spinner />
            </div>
          )}
          {status === 'error' && (
            <p className="p-4 text-sm text-danger">{text(errorCode)}</p>
          )}
          {status === 'done' && results.length === 0 && (
            <p className="p-4 text-sm text-faint">{text('empty')}</p>
          )}
          {status === 'done' && results.length > 0 && (
            <ul>
              {results.map((company) => {
                const followed = followedBySiret[company.siret] ?? null;
                return (
                  <li key={company.siret || company.name}>
                    <button
                      type="button"
                      className="block w-full px-4 py-3 text-left hover:bg-surfaceMuted"
                      onClick={() => onPick({ ...company, followed })}
                    >
                      <span className="block text-sm font-semibold text-ink">
                        {company.name}
                      </span>
                      <span className="block text-xs text-faint">
                        {text('resultLine', {
                          siret: formatSiret(company.siret),
                          city: company.city,
                        })}
                        {followed && ` · ${text('alreadyIn')}`}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="flex items-center justify-between gap-2 border-t border-line px-4 py-2 text-xs text-faint">
            <span>{text('footer')}</span>
            <button
              type="button"
              className="font-semibold text-ink hover:underline"
              onClick={() =>
                onPick({ manual: true, name: trimmed, followed: null })
              }
            >
              {text('manual')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDirectorySearch;
